import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://udischool.com.br";
const SITE_NAME = "UdiSchool";
const OG_IMAGE = `${SITE_URL}/assets/alexandre-duotone.png`;

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  robots?: string;
};

const HOME_SEO: SeoConfig = {
  title: "UdiSchool | Ingles para adultos em Uberlandia",
  description:
    "Descubra o que impede voce de falar ingles com confianca e receba um plano para avancar na Avaliacao Gratuita da UdiSchool.",
  path: "/",
};

const PUBLIC_ROUTES: Record<string, SeoConfig> = {
  "/": HOME_SEO,
  "/metodo": {
    title: "Metodo COMPASS | UdiSchool",
    description:
      "Conheca a metodologia COMPASS da UdiSchool: contato, orientacao, pratica ativa, acompanhamento e ingles para situacoes reais.",
    path: "/metodo",
  },
  "/trilhas": {
    title: "Trilhas de Ingles | UdiSchool",
    description:
      "Conheca as trilhas DESTRAVA, WORK e ACADEMY para desenvolver ingles oral, profissional e academico com direcionamento.",
    path: "/trilhas",
  },
  "/privacidade": {
    title: "Politica de Privacidade | UdiSchool",
    description:
      "Veja como a UdiSchool coleta, protege e utiliza dados enviados no site, no formulario e na Avaliacao Diagnostica.",
    path: "/privacidade",
  },
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value);
  }
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(id: string, data: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function configFor(pathname: string): SeoConfig {
  if (pathname.startsWith("/admin") || pathname.startsWith("/agendar")) {
    return {
      title: "UdiSchool",
      description: "Area interna da UdiSchool.",
      path: pathname,
      robots: "noindex, nofollow",
    };
  }
  return PUBLIC_ROUTES[pathname] ?? HOME_SEO;
}

export function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const config = configFor(pathname);
    const canonical = `${SITE_URL}${config.path === "/" ? "/" : config.path}`;
    const robots = config.robots ?? "index, follow, max-image-preview:large";

    document.documentElement.lang = "pt-BR";
    document.title = config.title;

    upsertMeta('meta[name="description"]', { name: "description", content: config.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="author"]', { name: "author", content: SITE_NAME });
    upsertMeta('meta[name="theme-color"]', { name: "theme-color", content: "#0d1b2a" });

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "pt_BR" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: config.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: config.description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: OG_IMAGE });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: "UdiSchool - ingles de verdade para jovens e adultos",
    });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: config.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: config.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: OG_IMAGE });

    upsertLink("canonical", canonical);

    if (!config.robots?.includes("noindex")) {
      upsertJsonLd("udi-jsonld-org", {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/favicon.svg`,
        image: OG_IMAGE,
        description: HOME_SEO.description,
        areaServed: ["Uberlandia", "Triangulo Mineiro", "Brasil"],
        sameAs: [],
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+55-34-99896-1974",
          contactType: "customer service",
          availableLanguage: ["Portuguese", "English"],
        },
      });
      upsertJsonLd("udi-jsonld-web", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: "pt-BR",
        potentialAction: {
          "@type": "ReserveAction",
          target: `${SITE_URL}/agendar/contato`,
          name: "Agendar Avaliacao Diagnostica",
        },
      });
    }
  }, [pathname]);

  return null;
}
